import map from "lodash/map";
import { convertFilterTagFromFormat } from "../../../agreed/filters";
import { ContentFormatV1 } from "../../../agreed/formats/v1/content_format";
import { upgradeContentToV2 } from "../../../agreed/formats/v1/migrations";
import { ContentFormatV2 } from "../../../agreed/formats/v2/content_format";
import { upgradeContentToV3 } from "../../../agreed/formats/v2/migrations";
import { ContentFormatV3 } from "../../../agreed/formats/v3/content_format";
import { upgradeContentToV4 } from "../../../agreed/formats/v3/migrations";
import { ContentFormatV4 } from "../../../agreed/formats/v4/content_format";
import { upgradeContentToV5 } from "../../../agreed/formats/v4/migrations";
import { ContentFormatV5 } from "../../../agreed/formats/v5/content_format";
import { upgradeContentToV6 } from "../../../agreed/formats/v5/migrations";
import { ContentFormatV6 } from "../../../agreed/formats/v6/content_format";
import { upgradeContentToV7 } from "../../../agreed/formats/v6/migrations";
import {
  ContentBookFormatV7,
  ContentFormatV7,
  ContentMuralFormatV7,
  ContentPrintFormatV7,
} from "../../../agreed/formats/v7/content_format";
import { Unit } from "../../../types/customization/box";
import { TemplateLocale } from "../../../types/customization/common";
import { Content, ContentCommon } from "../../../types/customization/content";
import { Template } from "../../../types/customization/template";

export function convertFromContentFormat(
  contentFormat:
    | ContentFormatV1
    | ContentFormatV2
    | ContentFormatV3
    | ContentFormatV4
    | ContentFormatV5
    | ContentFormatV6
    | ContentFormatV7,
  template: Template
): Content {
  if (contentFormat.definition_version === 7) {
    const contentCommon = {
      definitionVersion: contentFormat.definition_version,

      locale: contentFormat.locale,

      pages: map(contentFormat.pages, (page) => ({
        key: page.key,
        pageDefinitionKey: page.page_definition_key,

        backgroundColorKey: page.background_color_key,

        editableTexts: map(page.editable_texts, (element) => ({
          editableTextKey: element.editable_text_key,

          content: element.content,
          fontKey: element.font_key,
        })),

        editablePictures: map(page.editable_pictures, (element) => ({
          editablePictureKey: element.editable_picture_key,
          selectionPhotoKey: element.selection_photo_key,

          cropping: {
            default: element.cropping.default,

            x: { value: element.cropping.x, unit: Unit.P10K } as const,
            y: { value: element.cropping.y, unit: Unit.P10K } as const,

            height: {
              value: element.cropping.height,
              unit: Unit.P10K,
            } as const,
            width: { value: element.cropping.width, unit: Unit.P10K } as const,

            rotation: element.cropping.rotation,
          },

          filterTag: convertFilterTagFromFormat(element.filter_tag),
        })),
      })),

      selectionPhotos: map(contentFormat.selection_photos, (photo) => ({
        key: photo.key,

        height: { value: photo.height_px, unit: Unit.PX } as const,
        width: { value: photo.width_px, unit: Unit.PX } as const,

        localRef: photo.local_ref,

        fotomKey: photo.fotom_key,
        provider: photo.provider,
        providerRef: photo.provider_ref,
      })),
    };

    if (contentFormat.template_type === "book") {
      return {
        templateType: contentFormat.template_type,
        ...contentCommon,

        bookNumber: contentFormat.book_number,
        bookTitle: contentFormat.book_title,
      };
    } else if (contentFormat.template_type === "mural") {
      return {
        templateType: contentFormat.template_type,
        ...contentCommon,
      };
    } else if (contentFormat.template_type === "print") {
      return {
        templateType: contentFormat.template_type,
        ...contentCommon,

        printQuantities: map(contentFormat.print_quantities, (printQuantity) => ({
          pageKey: printQuantity.page_key,
          quantity: printQuantity.quantity,
        })),
      };
    } else {
      throw new Error(
        `Unsupported template_type "${(contentFormat as { template_type: string }).template_type}"`
      );
    }
  } else if (contentFormat.definition_version === 6) {
    return convertFromContentFormat(upgradeContentToV7(contentFormat), template);
  } else if (contentFormat.definition_version === 5) {
    return convertFromContentFormat(upgradeContentToV6(contentFormat, template), template);
  } else if (contentFormat.definition_version === 4) {
    return convertFromContentFormat(upgradeContentToV5(contentFormat), template);
  } else if (contentFormat.definition_version === 3) {
    return convertFromContentFormat(upgradeContentToV4(contentFormat), template);
  } else if (contentFormat.definition_version === 2) {
    return convertFromContentFormat(upgradeContentToV3(contentFormat), template);
  } else if (contentFormat.definition_version === 1) {
    return convertFromContentFormat(upgradeContentToV2(contentFormat), template);
  } else {
    throw new Error(
      `Unsupported definition_version "${
        (contentFormat as { definition_version: number }).definition_version
      }"`
    );
  }
}

export function convertToContentFormat(content: Content): ContentFormatV7 {
  if (content.definitionVersion === 7) {
    const contentCommon = {
      definition_version: content.definitionVersion,

      locale: content.locale,

      pages: map(content.pages, (page) => ({
        key: page.key,
        page_definition_key: page.pageDefinitionKey,

        background_color_key: page.backgroundColorKey,

        editable_texts: map(page.editableTexts, (element) => ({
          editable_text_key: element.editableTextKey,

          content: element.content,
          font_key: element.fontKey,
        })),

        editable_pictures: map(page.editablePictures, (element) => ({
          editable_picture_key: element.editablePictureKey,
          selection_photo_key: element.selectionPhotoKey,

          cropping: {
            default: element.cropping.default,

            x: element.cropping.x.value,
            y: element.cropping.y.value,

            height: element.cropping.height.value,
            width: element.cropping.width.value,

            rotation: element.cropping.rotation,
          },

          filter_tag: element.filterTag,
        })),
      })),

      selection_photos: map(content.selectionPhotos, (photo) => ({
        key: photo.key,

        height_px: photo.height.value,
        width_px: photo.width.value,

        local_ref: photo.localRef,

        fotom_key: photo.fotomKey,
        provider: photo.provider,
        provider_ref: photo.providerRef,
      })),
    };

    if (content.templateType === "mural") {
      const contentFormat: ContentMuralFormatV7 = {
        template_type: "mural",
        ...contentCommon,
      };

      return contentFormat;
    } else if (content.templateType === "print") {
      const contentFormat: ContentPrintFormatV7 = {
        template_type: "print",
        ...contentCommon,

        print_quantities: map(content.printQuantities, (printQuantity) => ({
          page_key: printQuantity.pageKey,
          quantity: printQuantity.quantity,
        })),
      };

      return contentFormat;
    } else if (content.templateType === "book") {
      const contentFormat: ContentBookFormatV7 = {
        template_type: "book",
        ...contentCommon,
      };
      if (content.bookNumber) {
        contentFormat.book_number = content.bookNumber;
      }
      if (content.bookTitle) {
        contentFormat.book_title = content.bookTitle;
        contentFormat.book_title_font_key = content.bookTitleFontKey;
      }

      return contentFormat;
    } else {
      throw new Error(
        `Unsupported templateType "${(content as { templateType: string }).templateType}"`
      );
    }
  } else {
    throw new Error(`Unsupported definitionVersion "${content.definitionVersion as string}"`);
  }
}

export function generateNewContent(template: Template, locale: TemplateLocale): Content {
  const contentCommon: ContentCommon = {
    definitionVersion: 7,
    locale,
    pages: [],
    selectionPhotos: [],
  };

  const templateType = template.templateType;
  if (templateType === "mural") {
    return {
      ...contentCommon,
      templateType,
    };
  } else if (templateType === "print") {
    return {
      ...contentCommon,
      printQuantities: [],
      templateType,
    };
  } else if (templateType === "book") {
    return {
      ...contentCommon,
      templateType,
    };
  } else {
    const exhaustiveCheck: never = templateType;
    throw new Error(`Unsupported templateType ${exhaustiveCheck as string}`);
  }
}
